import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Button,
  CardHeader,
  ErrorNotice,
  Field,
  LoadingState,
  Modal,
  Panel,
  Pill,
  SectionedChecklist,
  SelectInput,
  Tabs,
  TextInput,
  sectionTitlesOf,
  type ChecklistItem as SharedChecklistItem,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  CHECKLIST_ITEM_KIND,
  listChecklist,
  saveChecklist,
  type ChecklistItem,
  type ChecklistItemWrite,
} from '@/lib/events';
import { CLIENT_LIST, ClientChecklists } from './event-client-portal';

/**
 * Every checklist on the event, in one format, each saying who can see it:
 * BCE's own list from the program's template, marked internal, then the
 * two the client works from on their portal, marked shared.
 */

/** How a template item's tick and "not applicable" read as a status. */
const BCE_STATUS = {
  notStarted: 'NOT_STARTED',
  done: 'COMPLETED',
  notApplicable: 'NOT_REQUIRED',
} as const;
type BceStatus = (typeof BCE_STATUS)[keyof typeof BCE_STATUS];

const BCE_STATUSES: readonly (readonly [BceStatus, string])[] = [
  [BCE_STATUS.notStarted, 'Not started'],
  [BCE_STATUS.done, 'Done'],
  [BCE_STATUS.notApplicable, 'Not applicable'],
];
const BCE_SETTLED: ReadonlySet<string> = new Set([BCE_STATUS.done, BCE_STATUS.notApplicable]);

/** The template tag every program's items carry; anything else names a program family. */
const EVERY_EVENT_TAG = 'All';
const EVERY_EVENT_SECTION = 'Every event';

const NEW_SECTION = '__new__';

function statusOf(item: ChecklistItem): BceStatus {
  if (!item.applicable) return BCE_STATUS.notApplicable;
  return item.checked ? BCE_STATUS.done : BCE_STATUS.notStarted;
}

/** Template items group by the program family that put them on the list; hand-added ones by their heading. */
function sectionOf(item: ChecklistItem): string {
  if (item.kind === CHECKLIST_ITEM_KIND.custom) return item.section ?? EVERY_EVENT_SECTION;
  return item.tags.find((tag) => tag !== EVERY_EVENT_TAG) ?? EVERY_EVENT_SECTION;
}

function asSharedItem(item: ChecklistItem): SharedChecklistItem {
  return {
    id: item._id,
    title: item.name,
    status: statusOf(item),
    responsibilityKey: null,
    section: sectionOf(item),
    responsibleName: null,
    notes: null,
    dueAt: null,
    overdue: false,
    approvalRequired: false,
  };
}

function applyStatus(item: ChecklistItem, status: string): ChecklistItem {
  switch (status) {
    case BCE_STATUS.done:
      return { ...item, applicable: true, checked: true };
    case BCE_STATUS.notApplicable:
      return { ...item, applicable: false, checked: false };
    default:
      return { ...item, applicable: true, checked: false };
  }
}

/** An item as the list is written back: a template item by id, a hand-added one with its words. */
function asWrite(item: ChecklistItem): ChecklistItemWrite {
  return item.kind === CHECKLIST_ITEM_KIND.template
    ? { kind: CHECKLIST_ITEM_KIND.template, _id: item._id, checked: item.checked, applicable: item.applicable }
    : {
        kind: CHECKLIST_ITEM_KIND.custom,
        _id: item._id,
        name: item.name,
        ...(item.section ? { section: item.section } : {}),
        checked: item.checked,
        applicable: item.applicable,
      };
}

/** The three lists on the event, one sub-tab each. */
const LISTS = ['bce', 'planning', 'venue'] as const;
type List = (typeof LISTS)[number];

const LIST_ITEMS: readonly { readonly key: List; readonly label: string }[] = [
  { key: 'bce', label: 'BCE checklist' },
  { key: 'planning', label: 'Planning checklist' },
  { key: 'venue', label: 'Venue checklist' },
];

function asList(value: string | null): List {
  return (LISTS as readonly string[]).includes(value ?? '') ? (value as List) : 'bce';
}

export function ChecklistTab({
  eventId,
  clientPortal,
}: {
  readonly eventId: string;
  /** The client is on the portal, so their two lists exist. */
  readonly clientPortal: boolean;
}): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const list = clientPortal ? asList(params.get('list')) : 'bce';

  return (
    <div className="mt-6 space-y-5">
      {clientPortal ? (
        <Tabs<List>
          label="Checklists"
          items={LIST_ITEMS}
          active={list}
          onChange={(next) =>
            setParams(
              (previous) => {
                const nextParams = new URLSearchParams(previous);
                nextParams.set('list', next);
                return nextParams;
              },
              { replace: true },
            )
          }
        />
      ) : null}
      {list === 'bce' ? <BceChecklist eventId={eventId} /> : null}
      {list === 'planning' ? <ClientChecklists scope={{ kind: 'event', id: eventId }} list={CLIENT_LIST.plan} /> : null}
      {list === 'venue' ? <ClientChecklists scope={{ kind: 'event', id: eventId }} list={CLIENT_LIST.venue} /> : null}
    </div>
  );
}

/** The add or edit dialog: which item, and which section it opened on. */
interface ItemDialog {
  readonly editing: ChecklistItem | null;
  readonly lockedSection: string | null;
}

function BceChecklist({ eventId }: { readonly eventId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const queryKey = ['event-checklist', eventId];
  const checklist = useQuery({
    queryKey,
    queryFn: ({ signal }) => listChecklist(eventId, signal),
  });

  const save = useMutation({
    mutationFn: (writes: readonly ChecklistItemWrite[]) => saveChecklist(eventId, writes),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const [dialog, setDialog] = useState<ItemDialog | null>(null);
  const [name, setName] = useState('');
  const [sectionChoice, setSectionChoice] = useState('');
  const [newSectionName, setNewSectionName] = useState('');
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  if (checklist.isPending) return <LoadingState label="Loading checklist…" />;
  if (checklist.isError) {
    return <ErrorNotice title="Checklist unavailable" message={errorMessage(checklist.error)} />;
  }
  const { items, removed } = checklist.data;
  const shared = items.map(asSharedItem);
  const sectionTitles = sectionTitlesOf(shared);
  const busy = save.isPending;

  /** Writes the whole list with one item swapped for its new version. */
  function writeWith(next: (current: readonly ChecklistItem[]) => readonly ChecklistItemWrite[]): void {
    save.mutate(next(items));
  }

  function openAdd(lockedSection: string | null, asNew = false): void {
    setDialog({ editing: null, lockedSection });
    setName('');
    setSectionChoice(asNew ? NEW_SECTION : (lockedSection ?? sectionTitles[0] ?? EVERY_EVENT_SECTION));
    setNewSectionName('');
    setConfirmingRemove(false);
  }

  function openEdit(itemId: string): void {
    const item = items.find((candidate) => candidate._id === itemId);
    if (!item) return;
    setDialog({ editing: item, lockedSection: null });
    setName(item.name);
    setSectionChoice(sectionOf(item));
    setNewSectionName('');
    setConfirmingRemove(false);
  }

  function submit(): void {
    if (!dialog) return;
    const section = dialog.lockedSection ?? (sectionChoice === NEW_SECTION ? newSectionName.trim() : sectionChoice);
    const title = name.trim();
    if (title.length === 0 || section.length === 0) return;

    const editing = dialog.editing;
    if (editing?.kind === CHECKLIST_ITEM_KIND.template) {
      // A template item keeps its words; there is nothing to save but a removal.
      setDialog(null);
      return;
    }
    writeWith((current) => [
      ...current.filter((item) => item._id !== editing?._id).map(asWrite),
      {
        kind: CHECKLIST_ITEM_KIND.custom,
        ...(editing ? { _id: editing._id } : {}),
        name: title,
        section,
        checked: editing?.checked ?? false,
        applicable: editing?.applicable ?? true,
      },
    ]);
    setDialog(null);
  }

  /** Takes the open item off this event: a template item is marked removed, a hand-added one deleted. */
  function removeOpenItem(): void {
    const editing = dialog?.editing;
    if (!editing) return;
    writeWith((current) => {
      const rest = current.filter((item) => item._id !== editing._id).map(asWrite);
      return editing.kind === CHECKLIST_ITEM_KIND.template
        ? [...rest, { kind: CHECKLIST_ITEM_KIND.template, _id: editing._id, checked: false, applicable: true, removed: true }]
        : rest;
    });
    setDialog(null);
  }

  function restore(templateId: string): void {
    writeWith((current) => [
      ...current.map(asWrite),
      { kind: CHECKLIST_ITEM_KIND.template, _id: templateId, checked: false, applicable: true, removed: false },
    ]);
  }

  const editing = dialog?.editing ?? null;
  const templateItem = editing?.kind === CHECKLIST_ITEM_KIND.template;

  return (
    <Panel className="overflow-hidden">
      <div className="p-5 pb-4">
        <CardHeader
          className="mb-0"
          title="BCE checklist"
          description="The program's template items for the team delivering the day, plus anything added for this event. The client never sees this list."
          badge={<Pill tone="neutral">Internal</Pill>}
          aside={
            <span className="flex items-center gap-2">
              <Button variant="secondary" size="compact" onClick={() => openAdd(null, true)} disabled={busy}>
                Add section
              </Button>
              <Button size="compact" onClick={() => openAdd(null)} disabled={busy}>
                Add item
              </Button>
            </span>
          }
        />
      </div>
      <SectionedChecklist
        items={shared}
        statuses={BCE_STATUSES}
        doneStatus={BCE_STATUS.done}
        undoneStatus={BCE_STATUS.notStarted}
        settledStatuses={BCE_SETTLED}
        fallbackSectionTitle={EVERY_EVENT_SECTION}
        onSetStatus={(itemId, status) =>
          writeWith((current) => current.map((item) => asWrite(item._id === itemId ? applyStatus(item, status) : item)))
        }
        onAddItem={(section) => openAdd(section)}
        onAddSection={() => openAdd(null, true)}
        onEdit={(item) => openEdit(item.id)}
        isSaving={busy}
        emptyTitle="No checklist for this event"
        emptyMessage="Checklist items come from the program's template; this event's program has none. Add a section to start one by hand."
      />
      {removed.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-portal-line px-5 py-3 text-portal-micro text-portal-muted">
          <span>
            {removed.length === 1 ? '1 template item taken off this event:' : `${removed.length} template items taken off this event:`}
          </span>
          {removed.map((item) => (
            <button
              key={item._id}
              type="button"
              disabled={busy}
              onClick={() => restore(item._id)}
              className="font-bold text-brand hover:underline"
            >
              Restore {item.name}
            </button>
          ))}
        </div>
      ) : null}

      <Modal
        open={dialog !== null}
        title={editing ? (templateItem ? editing.name : 'Edit item') : dialog?.lockedSection ? `Add to ${dialog.lockedSection}` : sectionChoice === NEW_SECTION ? 'New section' : 'Add item'}
        subtitle={
          templateItem
            ? 'This item comes from the program template, so its wording is fixed. Take it off this event if it does not apply.'
            : 'Internal only. The client never sees the BCE checklist.'
        }
        onClose={() => setDialog(null)}
        actions={
          <>
            {editing ? (
              <Button
                variant="secondary"
                className="mr-auto text-rose-700"
                disabled={busy}
                onClick={() => {
                  if (!confirmingRemove) {
                    setConfirmingRemove(true);
                    return;
                  }
                  removeOpenItem();
                }}
              >
                {confirmingRemove ? (templateItem ? 'Yes, take it off' : 'Yes, delete it') : templateItem ? 'Remove from this event' : 'Delete'}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => setDialog(null)}>
              {templateItem ? 'Close' : 'Cancel'}
            </Button>
            {templateItem ? null : (
              <Button onClick={submit} disabled={busy || name.trim().length === 0 || (sectionChoice === NEW_SECTION && newSectionName.trim().length === 0)}>
                {editing ? 'Save' : 'Add'}
              </Button>
            )}
          </>
        }
      >
        {templateItem ? null : (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <Field label="Item" htmlFor="bce-checklist-name">
              <TextInput id="bce-checklist-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Confirm the dock with the venue" />
            </Field>
            {dialog?.lockedSection ? null : (
              <Field label="Section" htmlFor="bce-checklist-section">
                <SelectInput id="bce-checklist-section" value={sectionChoice} onChange={(event) => setSectionChoice(event.target.value)}>
                  {sectionTitles.map((title) => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  ))}
                  <option value={NEW_SECTION}>New section…</option>
                </SelectInput>
              </Field>
            )}
            {sectionChoice === NEW_SECTION && !dialog?.lockedSection ? (
              <Field label="Section name" htmlFor="bce-checklist-new-section">
                <TextInput id="bce-checklist-new-section" value={newSectionName} onChange={(event) => setNewSectionName(event.target.value)} placeholder="Day before" />
              </Field>
            ) : null}
          </form>
        )}
      </Modal>
    </Panel>
  );
}
